import { useState } from "react";
import { Check, X, Ban, Plus, Edit2, GripVertical, SlidersHorizontal } from "lucide-react";
import type { OnboardingQuestion, QuestionType } from "../../data";
import { useAdminData } from "../../hooks/useAdminData";
import { categories as categoriesApi } from "../../api/endpoints";
import { AddToast } from "../../components/feedback/Toast";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { FilterDrawer } from "../../components/ui/FilterDrawer";
import { PageHeader } from "../../components/ui/PageHeader";
import { SectionLabel } from "../../components/ui/SectionLabel";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { CORAL, F, T } from "../../theme/tokens";
import { TableWrap } from "../../components/ui/TableWrap";

// ── Content: categories and onboarding questions ─────────────────────────────

export function ContentView({ addToast }: { addToast: AddToast }) {
  const { PLATFORM_CATEGORIES, CATEGORY_ONBOARDING, refetchCategories } = useAdminData();
  const [tab, setTab] = useState<"list" | "new">("list");
  const [catStatusFilter, setCatStatusFilter] = useState("All");
  const [catDrawerOpen, setCatDrawerOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [questions, setQuestions] = useState<OnboardingQuestion[]>([
    { id: "q_id", label: "National ID or Iqama", type: "upload", required: true, hint: "JPG or PDF, max 5 MB" },
  ]);

  function addQuestion() {
    const id = `q_${Date.now()}`;
    setQuestions((p) => [...p, { id, label: "", type: "text", required: false }]);
  }

  function updateQuestion(id: string, field: keyof OnboardingQuestion, value: any) {
    setQuestions((p) => p.map((q) => q.id === id ? { ...q, [field]: value } : q));
  }

  function removeQuestion(id: string) {
    setQuestions((p) => p.filter((q) => q.id !== id));
  }

  function createCategory() {
    categoriesApi.create({ name: newName, slug: newSlug || undefined, active: true })
      // The onboarding questions built above used to be discarded on save — the
      // category was created without a single one of them.
      .then(async (category) => {
        for (const q of questions) {
          if (!q.label.trim()) continue;
          await categoriesApi.addQuestion(category.id, {
            label: q.label,
            type: q.type,
            required: q.required,
            options: q.options,
            placeholder: q.placeholder,
            hint: q.hint,
          });
        }
        refetchCategories();
        addToast(`Category "${newName}" created with ${questions.filter((q) => q.label.trim()).length} questions`, "success");
        setTab("list");
        setNewName("");
        setNewSlug("");
      })
      .catch((e) => addToast(e instanceof Error ? e.message : "Failed to create category", "error"));
  }

  function renameCategory(c: { id: string; name: string; slug: string }) {
    const name = window.prompt(`Rename "${c.name}" to:`, c.name);
    if (name === null || !name.trim() || name === c.name) return;

    categoriesApi.update(c.id, { name: name.trim() })
      .then(() => { refetchCategories(); addToast("Category renamed", "success"); })
      .catch((e) => addToast(e instanceof Error ? e.message : "Failed to rename category", "error"));
  }
  function toggleCategoryActive(id: string, active: boolean) {
    categoriesApi.update(id, { active: !active })
      .then(() => { refetchCategories(); addToast(!active ? "Enabled" : "Disabled", "info"); })
      .catch(() => addToast("Failed to update category", "error"));
  }

  const Q_TYPES: { value: QuestionType; label: string }[] = [
    { value: "text", label: "Short text" },
    { value: "textarea", label: "Long text" },
    { value: "select", label: "Single select" },
    { value: "multiselect", label: "Multi select" },
    { value: "upload", label: "File upload" },
    { value: "url", label: "URL" },
  ];

  return (
    <div>
      <FilterDrawer open={catDrawerOpen} onClose={() => setCatDrawerOpen(false)} title="Category"
        fields={[{ type: "chips", key: "status", label: "Status", options: ["All", "Active", "Inactive"] }]}
        values={{ status: catStatusFilter }}
        onApply={(v) => setCatStatusFilter(v.status ?? "All")} />

      <PageHeader title="Category" sub="Platform categories and onboarding configuration" actions={
        tab === "list"
          ? (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button className="filter-btn" onClick={() => setCatDrawerOpen(true)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 13px", borderRadius: "8px", fontSize: "12px", fontFamily: F, fontWeight: 500, cursor: "pointer", border: `1px solid ${catStatusFilter !== "All" ? CORAL : T.border}`, background: catStatusFilter !== "All" ? `rgba(209,0,75,0.06)` : T.s2, color: catStatusFilter !== "All" ? CORAL : T.sub, transition: "all 0.15s" }}>
                <SlidersHorizontal size={13} /> Filters
              </button>
              <Btn variant="primary" onClick={() => setTab("new")} iconOnlyPhone><Plus size={13} /> Add category</Btn>
            </div>
          )
          : <Btn variant="ghost" onClick={() => setTab("list")}>← Back to categories</Btn>
      } />

      {tab === "list" ? (
        <Card>
          <TableWrap><table style={{ width: "100%", borderCollapse: "collapse", fontFamily: F }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                {["Category", "Slug", "Talent count", "Onboarding Qs", "Status", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: T.muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PLATFORM_CATEGORIES.filter((c) => catStatusFilter === "All" || (catStatusFilter === "Active" ? c.active : !c.active)).map((c) => {
                const ob = CATEGORY_ONBOARDING.find((o) => o.categoryId === c.id);
                return (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${T.border}`, transition: "background 0.1s" }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = T.s2; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; }}>
                    <td style={{ padding: "12px 14px", fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F }}>{c.name}</td>
                    <td style={{ padding: "12px 14px" }}><span style={{ fontSize: "11px", fontFamily: "'Space Mono', monospace", color: T.sub }}>{c.slug}</span></td>
                    <td style={{ padding: "12px 14px", fontSize: "12px", color: T.text, fontFamily: F }}>{c.talentCount.toLocaleString()}</td>
                    <td style={{ padding: "12px 14px" }}>
                      {ob ? (
                        <span style={{ fontSize: "11px", color: "#1e40af", background: "#dbeafe", borderRadius: "9999px", padding: "2px 8px", fontFamily: F }}>{ob.questions.length} questions</span>
                      ) : (
                        <span style={{ fontSize: "11px", color: T.muted, fontFamily: F }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: "12px 14px" }}><StatusBadge status={c.active ? "Active" : "Inactive"} /></td>
                    <td style={{ padding: "12px 14px" }}>
                      <div style={{ display: "flex", gap: "4px" }}>
                        <Btn variant="ghost" size="xs" onClick={() => renameCategory(c)}><Edit2 size={10} /> Edit</Btn>
                        <Btn variant="ghost" size="xs" onClick={() => toggleCategoryActive(c.id, c.active)}>{c.active ? <Ban size={10} /> : <Check size={10} />}</Btn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></TableWrap>
        </Card>
      ) : (
        <div className="rg-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
          {/* Category info */}
          <Card style={{ padding: "22px" }}>
            <SectionLabel>Category details</SectionLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div>
                <p style={{ fontSize: "11px", color: T.muted, fontFamily: F, marginBottom: "5px" }}>Category name *</p>
                <input value={newName} onChange={(e) => { setNewName(e.target.value); setNewSlug(e.target.value.toLowerCase().replace(/\s+/g, "-")); }}
                  placeholder="e.g. Sound Designer" style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: "8px", padding: "9px 12px", fontSize: "13px", fontFamily: F, outline: "none", color: T.text, boxSizing: "border-box" }} />
              </div>
              <div>
                <p style={{ fontSize: "11px", color: T.muted, fontFamily: F, marginBottom: "5px" }}>Slug (auto-generated)</p>
                <input value={newSlug} onChange={(e) => setNewSlug(e.target.value)}
                  style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: "8px", padding: "9px 12px", fontSize: "12px", fontFamily: "'Space Mono', monospace", outline: "none", color: T.sub, boxSizing: "border-box" }} />
              </div>
              <div>
                <p style={{ fontSize: "11px", color: T.muted, fontFamily: F, marginBottom: "5px" }}>Arabic name (optional)</p>
                <input placeholder="e.g. مصمم صوت" dir="rtl" style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: "8px", padding: "9px 12px", fontSize: "13px", fontFamily: "var(--font-arabic, sans-serif)", outline: "none", color: T.text, boxSizing: "border-box" }} />
              </div>
              <Btn variant="primary" onClick={createCategory} disabled={!newName}
                style={{ justifyContent: "center", width: "100%" }}>
                <Plus size={13} /> Create category
              </Btn>
            </div>
          </Card>

          {/* Onboarding questions builder */}
          <Card style={{ padding: "22px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
              <SectionLabel>Onboarding questions</SectionLabel>
              <Btn variant="ghost" size="xs" onClick={addQuestion}><Plus size={10} /> Add question</Btn>
            </div>
            <p style={{ fontSize: "11px", color: T.sub, fontFamily: F, marginBottom: "14px" }}>These questions will be shown to talent when they select this category during registration.</p>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {questions.map((q, i) => (
                <div key={q.id} style={{ border: `1px solid ${T.border}`, borderRadius: "10px", padding: "14px", display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <GripVertical size={12} style={{ color: T.muted, flexShrink: 0 }} />
                    <span style={{ fontSize: "10px", color: T.muted, fontFamily: F }}>Q{i + 1}</span>
                    <div style={{ flex: 1 }} />
                    <label style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10px", color: T.sub, fontFamily: F, cursor: "pointer" }}>
                      <input type="checkbox" checked={q.required} onChange={(e) => updateQuestion(q.id, "required", e.target.checked)} />
                      Required
                    </label>
                    <button onClick={() => removeQuestion(q.id)} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, display: "flex" }}><X size={12} /></button>
                  </div>
                  <input value={q.label} onChange={(e) => updateQuestion(q.id, "label", e.target.value)}
                    placeholder="Question label…" style={{ border: `1px solid ${T.border}`, borderRadius: "6px", padding: "6px 10px", fontSize: "12px", fontFamily: F, outline: "none", color: T.text }} />
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <select value={q.type} onChange={(e) => updateQuestion(q.id, "type", e.target.value as QuestionType)}
                      style={{ flex: "1 1 140px", border: `1px solid ${T.border}`, borderRadius: "6px", padding: "5px 8px", fontSize: "11px", fontFamily: F, outline: "none", color: T.text, background: T.surface }}>
                      {Q_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    {(q.type === "select" || q.type === "multiselect") && (
                      <input value={q.options?.join(", ") ?? ""} onChange={(e) => updateQuestion(q.id, "options", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                        placeholder="Options, comma-separated" style={{ flex: "2 1 180px", border: `1px solid ${T.border}`, borderRadius: "6px", padding: "5px 8px", fontSize: "11px", fontFamily: F, outline: "none", color: T.text }} />
                    )}
                    {q.type === "upload" && (
                      <input value={q.hint ?? ""} onChange={(e) => updateQuestion(q.id, "hint", e.target.value)}
                        placeholder="Hint text (e.g. JPG or PDF, max 5 MB)" style={{ flex: "2 1 180px", border: `1px solid ${T.border}`, borderRadius: "6px", padding: "5px 8px", fontSize: "11px", fontFamily: F, outline: "none", color: T.sub }} />
                    )}
                  </div>
                </div>
              ))}
              {questions.length === 0 && (
                <div style={{ textAlign: "center", padding: "24px 0", color: T.muted, fontSize: "12px", fontFamily: F }}>
                  No questions yet. Click "Add question" to start.
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
